export interface TopSectionConfig {
  mode: 'banner_full' | 'banner_background';
  showLogo: boolean;
  showCompanyName: boolean;
  showFounded: boolean;
  showTitle: boolean;
  showSubtitle: boolean;
  title: string;
  subtitle: string;
  bannerImage: string;
  overlay: 'dark' | 'light' | 'none';
  alignment: 'center' | 'left';
}

export interface MainImageConfig {
  image: string;
  altText: string;
  position: 'center' | 'top' | 'bottom';
  overlayIntensity: number;
  showClientBox: boolean;
}

export interface ClientBoxConfig {
  show: boolean;
  title: string;
  showClientName: boolean;
  showCompany: boolean;
  showProposalNumber: boolean;
  showDate: boolean;
  clientName: string;
  clientCompany: string;
  proposalNumber: string;
  date: string;
}

export interface DifferentialConfig {
  text: string;
  show: boolean;
}

export interface InstitutionalConfig {
  show: boolean;
  text: string;
  foundedYear: number;
  differentials: DifferentialConfig[];
  showCustomDifferential: boolean;
  customDifferential: string;
}

export interface QRCodeConfig {
  mode: 'manual' | 'auto';
  image: string;
  enabled: boolean;
}

export interface FooterConfig {
  mode: 'banner_full' | 'dynamic';
  show: boolean;
  bannerImage: string;
  overlay: 'dark' | 'light' | 'none';
  qrCode: QRCodeConfig;
  showPhones: boolean;
  showWhatsApp: boolean;
  showEmail: boolean;
  showWebsite: boolean;
  showCity: boolean;
  showCNPJ: boolean;
  showHours: boolean;
  showSpecialties: boolean;
}

export interface SpecialtyConfig {
  name: string;
  icon: string;
  active: boolean;
  order: number;
}

export interface SEOConfig {
  noindex: boolean;
  nofollow: boolean;
  title: string;
  description: string;
  keywords: string;
}

export interface CoverConfig {
  enabled: boolean;
  image: string;
  overlay: 'dark' | 'light' | 'none';
  title: string;
  subtitle: string;
}

export interface BackCoverConfig {
  enabled: boolean;
  image: string;
  overlay: 'dark' | 'light' | 'none';
  message: string;
}

export interface PDFConfig {
  includePageNumbers: boolean;
}

export interface TemplateConfig {
  top: TopSectionConfig;
  mainImage: MainImageConfig;
  clientBox: ClientBoxConfig;
  institutional: InstitutionalConfig;
  footer: FooterConfig;
  specialties: SpecialtyConfig[];
  seo: SEOConfig;
  cover: CoverConfig;
  backCover: BackCoverConfig;
  pdf: PDFConfig;
  activePresetId: string | null;
}

export const defaultTemplateConfig: TemplateConfig = {
  top: {
    mode: 'banner_background',
    showLogo: true,
    showCompanyName: true,
    showFounded: true,
    showTitle: true,
    showSubtitle: true,
    title: 'PROPOSTA COMERCIAL',
    subtitle: 'Solução personalizada para sua necessidade',
    bannerImage:
      'https://readdy.ai/api/search-image?query=Modern%20luxury%20house%20at%20night%20with%20security%20cameras%20mounted%20on%20exterior%20walls%2C%20warm%20ambient%20lighting%2C%20dark%20blue%20evening%20sky%2C%20professional%20security%20system%20installation%20scene%2C%20elegant%20architecture%20with%20glass%20windows%2C%20subtle%20landscape%20lighting%2C%20premium%20residential%20security%20atmosphere%2C%20photorealistic%20architectural%20photography%20style%2C%20dark%20moody%20tones%20with%20golden%20warm%20light%20accents&width=1400&height=900&seq=1&orientation=landscape',
    overlay: 'dark',
    alignment: 'center',
  },
  mainImage: {
    image:
      'https://readdy.ai/api/search-image?query=Modern%20luxury%20house%20at%20night%20with%20security%20cameras%20mounted%20on%20exterior%20walls%2C%20warm%20ambient%20lighting%2C%20dark%20blue%20evening%20sky%2C%20professional%20security%20system%20installation%20scene%2C%20elegant%20architecture%20with%20glass%20windows%2C%20subtle%20landscape%20lighting%2C%20premium%20residential%20security%20atmosphere%2C%20photorealistic%20architectural%20photography%20style%2C%20dark%20moody%20tones%20with%20golden%20warm%20light%20accents&width=1400&height=900&seq=1&orientation=landscape',
    altText: 'Segurança residencial',
    position: 'center',
    overlayIntensity: 60,
    showClientBox: false,
  },
  clientBox: {
    show: false,
    title: 'DADOS DO CLIENTE',
    showClientName: true,
    showCompany: true,
    showProposalNumber: true,
    showDate: true,
    clientName: 'João da Silva',
    clientCompany: 'Empresa Exemplo Ltda',
    proposalNumber: 'PROP-2025-001',
    date: '31/05/2025',
  },
  institutional: {
    show: true,
    text: 'Há {{years}} anos desenvolvendo soluções em segurança eletrônica e automação.',
    foundedYear: 1993,
    differentials: [
      { text: '{{years}} anos de mercado', show: true },
      { text: 'Projetos personalizados', show: true },
      { text: 'Atendimento residencial', show: true },
      { text: 'Atendimento comercial', show: true },
      { text: 'Atendimento para indústrias', show: true },
      { text: 'Soluções para sítios e fazendas', show: true },
      { text: 'Assistência técnica especializada', show: true },
      { text: 'Equipe experiente e qualificada', show: true },
    ],
    showCustomDifferential: false,
    customDifferential: '',
  },
  footer: {
    mode: 'dynamic',
    show: true,
    bannerImage: '',
    overlay: 'dark',
    qrCode: {
      mode: 'manual',
      image:
        'https://readdy.ai/api/search-image?query=QR%20code%20mockup%20on%20dark%20background%2C%20simple%20black%20and%20white%20QR%20code%20square%2C%20clean%20minimalist%20design%2C%20professional%20business%20card%20style%2C%20no%20text%20around%20the%20QR%20code%2C%20high%20contrast%20black%20and%20white%20pattern&width=200&height=200&seq=2&orientation=squarish',
      enabled: true,
    },
    showPhones: true,
    showWhatsApp: true,
    showEmail: true,
    showWebsite: true,
    showCity: true,
    showCNPJ: true,
    showHours: true,
    showSpecialties: true,
  },
  specialties: [
    { name: 'CÂMERAS DE SEGURANÇA', icon: 'ri-camera-lens-line', active: true, order: 1 },
    { name: 'ALARMES', icon: 'ri-shield-check-line', active: true, order: 2 },
    { name: 'CERCA ELÉTRICA', icon: 'ri-flashlight-line', active: true, order: 3 },
    { name: 'PORTÕES ELETRÔNICOS', icon: 'ri-door-closed-line', active: true, order: 4 },
    { name: 'SEGURANÇA RURAL', icon: 'ri-plant-line', active: true, order: 5 },
    { name: 'ENERGIA SOLAR', icon: 'ri-sun-line', active: false, order: 6 },
    { name: 'FIBRA ÓPTICA', icon: 'ri-wifi-line', active: false, order: 7 },
    { name: 'AUTOMAÇÃO', icon: 'ri-home-smile-line', active: false, order: 8 },
  ],
  seo: {
    noindex: false,
    nofollow: false,
    title: 'Eletrônica 2000 - Portal de Propostas',
    description: 'Portal de propostas comerciais da Eletrônica 2000. Segurança eletrônica, automação e energia solar desde 1993.',
    keywords: 'propostas, segurança eletrônica, automação, câmeras, alarmes, Campo Mourão',
  },
  cover: {
    enabled: true,
    image: '',
    overlay: 'dark',
    title: 'PROPOSTA COMERCIAL',
    subtitle: 'Solução personalizada para sua necessidade',
  },
  backCover: {
    enabled: false,
    image: '',
    overlay: 'dark',
    message: 'Agradecemos sua preferência. Entre em contato para mais informações.',
  },
  pdf: {
    includePageNumbers: false,
  },
  activePresetId: null,
};

export function getExperienceYears(foundedYear: number): number {
  return new Date().getFullYear() - foundedYear;
}

export function replaceYears(template: string, foundedYear: number): string {
  const years = getExperienceYears(foundedYear);
  const yearsText = years >= 30 ? `mais de ${years}` : `${years}`;
  return template.replace(/\{\{years\}\}/g, yearsText);
}

export function getOverlayClass(overlay: 'dark' | 'light' | 'none'): string {
  switch (overlay) {
    case 'dark':
      return 'bg-gradient-to-b from-brand-dark/80 via-brand-dark/60 to-brand-dark/90';
    case 'light':
      return 'bg-gradient-to-b from-white/20 via-white/10 to-white/20';
    case 'none':
      return '';
    default:
      return 'bg-gradient-to-b from-brand-dark/80 via-brand-dark/60 to-brand-dark/90';
  }
}

export function getOverlayStyle(overlay: 'dark' | 'light' | 'none'): React.CSSProperties {
  switch (overlay) {
    case 'dark':
      return { background: 'linear-gradient(to bottom, rgba(10,22,40,0.8), rgba(10,22,40,0.6), rgba(10,22,40,0.9))' };
    case 'light':
      return { background: 'linear-gradient(to bottom, rgba(255,255,255,0.2), rgba(255,255,255,0.1), rgba(255,255,255,0.2))' };
    case 'none':
      return {};
    default:
      return { background: 'linear-gradient(to bottom, rgba(10,22,40,0.8), rgba(10,22,40,0.6), rgba(10,22,40,0.9))' };
  }
}

export function getImagePositionClass(position: 'center' | 'top' | 'bottom'): string {
  switch (position) {
    case 'top':
      return 'object-top';
    case 'bottom':
      return 'object-bottom';
    case 'center':
    default:
      return 'object-center';
  }
}

/**
 * Gera um token seguro aleatório para acesso às propostas.
 * NÃO utiliza IDs sequenciais. Token único, difícil de adivinhar, compartilhável.
 * Formato: 16 caracteres hexadecimais (ex: 9b31f4f6e8d3a7b2)
 * Futuro: pode ser expandido para incluir expiração, JWT, ou hash criptográfico.
 */
export function generateProposalToken(): string {
  const array = new Uint8Array(8);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(array);
  } else {
    // Fallback para ambientes sem crypto.getRandomValues
    for (let i = 0; i < array.length; i++) {
      array[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(array)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Valida se um token de proposta está no formato correto.
 * Aceita qualquer string alfanumérica de 4 a 64 caracteres.
 */
export function isValidProposalToken(token: string): boolean {
  if (!token || typeof token !== 'string') return false;
  return /^[a-zA-Z0-9_-]{4,64}$/.test(token);
}